<script setup lang="ts">
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { computed } from 'vue';
import { AppTypography } from '~/components/base';
import { useElection } from '~/composables/useElection';

// customParseFormat isn't registered globally (see app/plugins/dayjs.ts), so
// rather than parsing the already-formatted display string back into a date,
// derive the weekday straight from the election date.
dayjs.extend(utc);

/**
 * Two Figma treatments of the countdown:
 * - `strip` (desktop): full-width dark-green bar between topbar and hero.
 * - `card` (mobile): white rounded card on the sand with a tan DAYS LEFT badge.
 */
const { variant = 'strip' } = defineProps<{ variant?: 'strip' | 'card' }>();

const { election, countdown } = useElection();
const title = computed(() => election.value?.title);
const daysLeft = computed(() => countdown.value.daysLeft);
const daysLeftLabel = computed(() => countdown.value.daysLeftLabel);
const formattedDate = computed(() => countdown.value.formattedDate);
const weekday = computed(() =>
  election.value?.election_date
    ? dayjs.utc(election.value.election_date).format('dddd')
    : '',
);

// No API reply yet (pending, failed, or missing election_date) — hide rather
// than show stale/placeholder data.
const visible = computed(
  () => !!election.value?.election_date && countdown.value.status !== 'past',
);
</script>

<template>
  <!-- The visibility check lives on the root elements (not a wrapping
       <template v-if>) so each branch stays a single-root element and
       fallthrough attrs like `class="hidden lg:block"` are inherited —
       a root-level <template> wrapper silently drops them, which made the
       desktop-only strip render (invisibly) on mobile and push the sand
       hero 40px down from under the transparent topbar. -->
  <section v-if="visible && variant === 'strip'" class="election-banner w-full">
    <!-- Figma lays this out as four siblings in one row with a 16px gap, each
         with its own weight and colour — rather than one sentence with inline
         <strong>. The weekday takes the remaining space and right-aligns, which
         is what pushes the date group to the far edge. -->
    <div class="layout-size flex items-center gap-4 py-6">
      <AppTypography
        v-if="countdown.status === 'ongoing'"
        type="body-bold"
        class="election-banner__count shrink-0 whitespace-nowrap"
      >
        {{ title }} is On-going
      </AppTypography>
      <template v-else>
        <AppTypography
          type="body-bold"
          class="election-banner__count shrink-0 whitespace-nowrap"
        >
          {{ daysLeftLabel }} left
        </AppTypography>
        <AppTypography type="body" class="shrink-0 text-white">
          until the {{ title }}
        </AppTypography>
      </template>
      <AppTypography
        type="body"
        class="hidden min-w-0 flex-1 text-right text-white lg:block"
      >
        {{ weekday }}
      </AppTypography>
      <AppTypography
        type="body-bold"
        class="hidden shrink-0 whitespace-nowrap text-white lg:block"
      >
        {{ formattedDate }}
      </AppTypography>
    </div>
  </section>

  <div
    v-else-if="visible"
    class="election-banner-card flex items-center justify-between gap-3"
  >
    <div class="flex min-w-0 flex-1 flex-col gap-1">
      <AppTypography type="body-semibold">
        {{ title }}
      </AppTypography>
      <AppTypography type="footnote" class="election-banner-card__subtitle">
        {{ weekday }} {{ formattedDate }}
      </AppTypography>
    </div>
    <span
      v-if="countdown.status === 'ongoing'"
      class="election-banner-card__badge shrink-0"
    >
      <AppTypography type="footnote-bold" class="uppercase">
        On-going
      </AppTypography>
    </span>
    <span v-else class="election-banner-card__badge shrink-0">
      <strong class="election-banner-card__count">{{ daysLeft }}</strong>
      <AppTypography type="footnote-bold" class="uppercase">
        Days Left
      </AppTypography>
    </span>
  </div>
</template>

<style scoped lang="scss">
.election-banner {
  background-color: $surface-color-brand;
  // Figma rounds only the bottom corners — the strip tucks up under the
  // header, so its top edge stays square.
  border-bottom-right-radius: $radius-lg;
  border-bottom-left-radius: $radius-lg;

  // Figma tints the days-left figure with complementary/shade-03 rather than
  // plain white, so it reads as the emphasised part of the sentence.
  &__count {
    color: $complementary-color-shade-3;
  }
}

.election-banner-card {
  padding: $spacing-lg;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;

  &__subtitle {
    color: #201e1a;
    font-weight: 400;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-xs;
    padding: $spacing-md;
    border-radius: $radius-sm;
    background-color: #e8d9bd;
  }

  &__count {
    @include font-title-6;
  }
}
</style>
